import { CSVLink } from "react-csv";
import { IProtocolRuntime, IProtocolStack } from "./api/restModel";
import {
  NumberFormatter,
  createProtocolFilter,
  pad,
  showProtocol,
  toDateTimeFormat,
  toDateTimeFormatPlain,
  toHHMM,
} from "./utils/commonUtils";
import { Theme } from "@mui/material/styles";
import { loadProtocol } from "./api/restFacade";
import { zebStyles as parentZebStyles } from "./theme/zebstyles";
import { useAuth } from "oidc-react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import GetAppIcon from "@mui/icons-material/GetApp";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import React, { useEffect, useState } from "react";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";
import useAPIError from "./Dialogs/error/useAPIError";

const useStyles = makeStyles((theme: Theme) => ({
  ...parentZebStyles(theme),
  mainHeader: {
    "& > *": {
      padding: theme.spacing(2),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      boxSizing: "unset",
    },
  },
  mainRow: {
    "& > *": {
      borderBottom: "unset",
      padding: theme.spacing(2),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(0),
    },
  },
  mainRowSecond: {
    "& > *": {
      borderBottom: "unset",
      padding: theme.spacing(2),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(0),
    },
  },
  detailRow: {
    "& > *": {
      borderBottom: "unset",
      paddingTop: theme.spacing(0),
      paddingBottom: theme.spacing(0),
    },
  },
  cell: {
    padding: theme.spacing(1),
    margin: 0,
    border: 0,
  },
  formControlContainer: {
    alignItems: "center",
    display: "flex",
  },
  formControlYear: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(10),
    marginBottom: theme.spacing(4),
    minWidth: 120,
  },
  formControlMonth: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(4),
    marginBottom: theme.spacing(4),
    minWidth: 120,
  },
  formControlCSVExport: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(4),
    marginBottom: theme.spacing(4),
    minWidth: 120,
  },
  csvExportLink: {
    marginTop: theme.spacing(0),
    textDecoration: "none",
    color: theme.palette.primary.main,
  },
  paper: {
    padding: 0,
  },
  footer: {
    marginLeft: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    fontSize: 10,
  },
}));

//row with one stack and detailows with runtimes
function Row(props: { index: number; row: IProtocolStack }) {
  const [rowDetailOpen, setRowDetailOpen] = useState(getRowOpend);
  const classes = useStyles();

  function getRowOpend(): boolean {
    const result = localStorage.getItem("protocolrow_" + props.index);

    if (result === null) {
      localStorage.setItem("protocolrow_" + props.index, "false");
      return false;
    } else {
      return result === "true" ? true : false;
    }
  }

  function handleOpenDetail() {
    setRowDetailOpen(!rowDetailOpen);
    localStorage.setItem("protocolrow_" + props.index, (!rowDetailOpen).toString());
  }

  const rowDetail = props.row.runtimes.map((detailRow: IProtocolRuntime, index) => (
    <TableRow className={classes.mainRowSecond} key={index}>
      <TableCell align="left"></TableCell>
      <TableCell align="left"></TableCell>
      <TableCell align="left">{toDateTimeFormat(new Date(Date.parse(detailRow.starttime)))}</TableCell>
      <TableCell align="left">{detailRow.startuser}</TableCell>
      <TableCell align="left">{toDateTimeFormat(new Date(Date.parse(detailRow.stoptime)))}</TableCell>
      <TableCell align="left">{detailRow.stopuser}</TableCell>
      <TableCell align="right">
        <Tooltip title={toHHMM(detailRow.durationhours * 3600)} placement="right">
          <div>{NumberFormatter.format(detailRow.durationhours)} h</div>
        </Tooltip>
      </TableCell>
      <TableCell></TableCell>
    </TableRow>
  ));

  return (
    <React.Fragment>
      <TableRow className={classes.mainRow}>
        <TableCell align="left">
          <IconButton aria-label="expand row" size="small" onClick={() => handleOpenDetail()}>
            {rowDetailOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell align="left">{props.row.stackdisplayname}</TableCell>
        <TableCell align="left"></TableCell>
        <TableCell align="left"></TableCell>
        <TableCell align="left"></TableCell>
        <TableCell align="left"></TableCell>
        <TableCell align="right" style={{ fontWeight: "bold" }}>
          <Tooltip title={toHHMM(props.row.runtimessumhours * 3600)} placement="right">
            <div>{NumberFormatter.format(props.row.runtimessumhours)} h</div>
          </Tooltip>
        </TableCell>
        <TableCell></TableCell>
      </TableRow>
      {rowDetailOpen ? rowDetail : null}
    </React.Fragment>
  );
}

function determineSumOverall(stacks: IProtocolStack[]): number {
  let sum = 0 as number;
  stacks.forEach((stack) => {
    sum = sum + stack.runtimessumhours;
  });
  return sum;
}

function convertTofilterDate(year: string, month: string, day: string): string {
  const y = Number(year);
  const m = Number(month);
  let d = Number(day);

  if (d >= 28) {
    //correct last day of month
    const date = new Date(y, m, 0);
    d = Number(date.getDate());
  }

  return year + pad(m, 2) + pad(d, 2);
}

export default function Journal() {
  const classes = useStyles();
  const { addError } = useAPIError();

  const [selectedYear, setSelectedYear] = React.useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = React.useState((new Date().getMonth() + 1).toString());

  const [rows, setRows] = useState<IProtocolStack[]>([]);

  const [csvData, setCsvData] = useState<string[][]>([]);

  const [waitingLoading, setWaitingLoading] = useState(false);

  const [sumOverall, setSumOverall] = useState(0);
  const auth = useAuth();

  function loadProtocolData(from: string, to: string) {
    setWaitingLoading(true);

    const emptyArray = [] as IProtocolStack[];
    setRows(emptyArray);
    setSumOverall(0);

    loadProtocol(createProtocolFilter(from, to, []), auth)
      .then((data) => {
        if (data != null) {
          setRows(data);
          setSumOverall(determineSumOverall(data));
          setCsvData(generateCSVData(data));
        }
      })
      .catch((error) => {
        addError(error.message, "error");
      })
      .finally(() => setWaitingLoading(false));
  }

  useEffect(() => {
    if (auth && auth.userData) {
      loadProtocolData(
        convertTofilterDate(selectedYear, selectedMonth, "01"),
        convertTofilterDate(selectedYear, selectedMonth, "31")
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  function generateCSVData(stacks: IProtocolStack[]): string[][] {
    const result: string[][] = [["Environment", "Start Time", "Start User", "Stop Time", "Stop User", "Running hours"]];

    stacks.forEach((stack) => {
      stack.runtimes.forEach((runtime) => {
        const row: string[] = [];
        row.push(stack.stackdisplayname);
        row.push(toDateTimeFormat(new Date(Date.parse(runtime.starttime))));
        row.push(runtime.startuser);
        row.push(toDateTimeFormat(new Date(Date.parse(runtime.stoptime))));
        row.push(runtime.stopuser);
        row.push(NumberFormatter.format(runtime.durationhours));
        result.push(row);
      });
    });

    return result;
  }

  const handleChangeYear = (event: SelectChangeEvent<string>) => {
    setSelectedYear(event.target.value as string);
    loadProtocolData(
      convertTofilterDate(event.target.value as string, selectedMonth, "01"),
      convertTofilterDate(event.target.value as string, selectedMonth, "31")
    );
  };

  const handleChangeMonth = (event: SelectChangeEvent<string>) => {
    setSelectedMonth(event.target.value as string);
    loadProtocolData(
      convertTofilterDate(selectedYear, event.target.value as string, "01"),
      convertTofilterDate(selectedYear, event.target.value as string, "31")
    );
  };

  const csvExportFileName = () => {
    const now = new Date();
    return "ServiceProtocol_" + selectedYear + selectedMonth + "_" + toDateTimeFormatPlain(now) + ".csv";
  };

  const currentyear = new Date().getFullYear() as number;

  if (!showProtocol(auth)) {
    return <div>You do not have the necessary rights</div>;
  }

  return (
    <Paper>
      <div className={classes.formControlContainer}>
        <FormControl variant="standard" className={classes.formControlYear}>
          <InputLabel id="label-year">Year</InputLabel>
          <Select
            variant="standard"
            labelId="label-year"
            id="demo-simple-select-outlined"
            value={selectedYear}
            onChange={handleChangeYear}
            label="Year">
            <MenuItem value={currentyear}>{currentyear}</MenuItem>
            <MenuItem value={currentyear - 1}>{currentyear - 1}</MenuItem>
            <MenuItem value={currentyear - 2}>{currentyear - 2}</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="standard" className={classes.formControlMonth}>
          <InputLabel id="label-month">Month</InputLabel>
          <Select
            variant="standard"
            labelId="label-month"
            id="demo-simple-select-outlined"
            value={selectedMonth}
            onChange={handleChangeMonth}
            label="Month">
            <MenuItem value={1}>January</MenuItem>
            <MenuItem value={2}>February</MenuItem>
            <MenuItem value={3}>March</MenuItem>
            <MenuItem value={4}>April</MenuItem>
            <MenuItem value={5}>May</MenuItem>
            <MenuItem value={6}>June</MenuItem>
            <MenuItem value={7}>July</MenuItem>
            <MenuItem value={8}>August</MenuItem>
            <MenuItem value={9}>September</MenuItem>
            <MenuItem value={10}>October</MenuItem>
            <MenuItem value={11}>November</MenuItem>
            <MenuItem value={12}>December</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="standard" className={classes.formControlCSVExport}>
          <CSVLink data={csvData} separator={";"} className={classes.csvExportLink} filename={csvExportFileName()}>
            <Button className={classes.iconButton} startIcon={<GetAppIcon />}>
              {"CSV"}
            </Button>
          </CSVLink>
        </FormControl>
      </div>
      <TableContainer>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow className={classes.mainHeader}>
              <TableCell align="center" style={{ width: 30 }}>
                {waitingLoading ? <CircularProgress className={classes.circularProgress} size="5" /> : ""}
              </TableCell>
              <TableCell align="left" style={{ width: 300 }}>
                Environment
              </TableCell>
              <TableCell align="left" style={{ width: 200 }}>
                Start
              </TableCell>
              <TableCell align="left" style={{ width: 200 }}>
                Start User
              </TableCell>
              <TableCell align="left" style={{ width: 200 }}>
                Stop
              </TableCell>
              <TableCell align="left" style={{ width: 200 }}>
                Stop User
              </TableCell>
              <TableCell align="right" style={{ width: 200 }}>
                Running Hours
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {rows.map((row, index) => (
                <Row index={index} key={row.stackid} row={row} />
              ))}
            </>

            <TableRow className={classes.mainRow}>
              <TableCell align="left"></TableCell>
              <TableCell align="left"></TableCell>
              <TableCell align="left"></TableCell>
              <TableCell align="left"></TableCell>
              <TableCell align="left"></TableCell>
              <TableCell align="left"></TableCell>
              <TableCell align="right" style={{ width: 200, fontWeight: "bold" }}>
                <hr></hr>
                <Tooltip title={toHHMM(sumOverall * 3600)} placement="right">
                  <div>{NumberFormatter.format(sumOverall)} h</div>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <br></br>
      <Typography className={classes.footer}>
        *Die im Protokoll aufgeführten Laufzeiten der jeweiligen Umgebungen dienen als Orientierung der Gesamtlaufzeiten pro
        Monat. Die exakten Laufzeiten können davon abweichen. Entnehmen Sie diese bitte Ihrer Rechnung.
      </Typography>
    </Paper>
  );
}
